import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Coins, Bitcoin, BadgeCheck, Users, Rocket, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8c4c8cd5-6c40-4e6b-9b8a-de1282617ec2/images/95db3b2dd70a6b5ec0f3365135537f795c219dc44055a2684a97a9f82a194bdc.jpeg";
const CREATOR_IMG = "https://static.prod-images.emergentagent.com/jobs/8c4c8cd5-6c40-4e6b-9b8a-de1282617ec2/images/4900da0c9838be704008fa085fb1c3088e398d2cb289d66dfd2c01e6e2ff5442.jpeg";
const BANNER = "https://static.prod-images.emergentagent.com/jobs/8c4c8cd5-6c40-4e6b-9b8a-de1282617ec2/images/e76760226ad6137cf2f279e19fbe4b2e90c7189eb60320354adbad5be85d773e.jpeg";

const PERKS = [
  { icon: Coins, title: "Keep 100% of your earnings", text: "Every dollar from subscriptions and cash tips is yours. We never take a cut of what your fans pay you." },
  { icon: Crown, title: "$19.99/mo membership — FREE", text: "As a Founding Creator, your monthly platform membership is completely free until we fully launch." },
  { icon: Bitcoin, title: "Get paid in crypto", text: "Cash out in USDT — no mainstream bank rejecting you for being an adult creator. Discreet and reliable." },
  { icon: BadgeCheck, title: "Founding verified badge", text: "A permanent gold Founding Creator badge and featured placement so fans discover you first." },
  { icon: Users, title: "Built-in audience", text: "Dating, matching and live lounges mean members find you — you're not shouting into the void." },
  { icon: Rocket, title: "No de-platforming", text: "Post freely on an adult-friendly platform built for you — no surprise bans like on mainstream apps." },
];

const STEPS = [
  "Create your account & verify you're 18+",
  "Turn on Creator Studio and activate your FREE Founding membership",
  "Set your subscription price and upload your first post",
  "Share your link and start earning — you keep 100%",
];

export default function FoundingCreators() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cta = () => navigate(user ? "/profile" : "/auth");

  return (
    <div data-testid="founding-creators-page" className="min-h-screen relative z-10" style={{ background: "#0A0A0A" }}>
      {/* Header */}
      <header className="flex items-center justify-between px-6 md:px-12 py-6 relative z-20">
        <button onClick={() => navigate("/")} className="text-left">
          <div className="overline">Founding Creators · 18+</div>
          <div className="font-heading text-2xl md:text-3xl gold-text">Pleasure Empire</div>
        </button>
        <button data-testid="fc-header-cta" onClick={cta} className="btn-outline-gold px-5 py-2 rounded-full text-sm">
          {user ? "Go to my profile" : "Sign in"}
        </button>
      </header>

      {/* Hero */}
      <section className="relative min-h-[74vh] flex items-center px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-70" style={{ filter: "brightness(1.15)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0A0A0A 15%, rgba(10,10,10,0.4) 60%, rgba(10,10,10,0.05))" }} />
        </div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="relative z-10 max-w-2xl">
          <div className="overline mb-4">A limited founding invitation</div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl leading-[0.98] mb-5">
            Become a <span className="shimmer-gold italic">Founding Creator</span>
          </h1>
          <p className="text-[#D0D0D0] text-base md:text-lg max-w-xl mb-4">
            Keep <b className="text-white">100% of everything you earn</b>. Your <b className="text-white">$19.99/mo membership is free</b> until we fully launch. Get paid in crypto — no bank drama, no bans.
          </p>
          <div className="flex flex-wrap gap-3">
            <button data-testid="fc-hero-cta" onClick={cta} className="btn-gold px-7 py-3 rounded-full text-base font-medium">
              Claim your Founding spot 👑
            </button>
          </div>
          <div className="text-xs text-[#8A8A8A] mt-3">18+ only. Limited Founding Creator spots.</div>
        </motion.div>
      </section>

      {/* Perks */}
      <section className="px-6 md:px-12 py-16 max-w-6xl mx-auto">
        <div className="overline mb-2">Why creators are joining</div>
        <h2 className="font-heading text-3xl md:text-4xl mb-10">The most creator-friendly deal in adult</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PERKS.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              data-testid={`fc-perk-${i}`} className="pe-card rounded-2xl p-6">
              <p.icon className="text-[#D4AF37] mb-3" size={24} />
              <div className="font-heading text-xl mb-1.5">{p.title}</div>
              <div className="text-sm text-[#A0A0A0]">{p.text}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Split: how it works */}
      <section className="px-6 md:px-12 py-12 max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
          <div className="overline mb-2">Live in 10 minutes</div>
          <h2 className="font-heading text-3xl md:text-4xl mb-6">How to get started</h2>
          <div className="space-y-4">
            {STEPS.map((s, i) => (
              <div key={i} data-testid={`fc-step-${i}`} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full gold-gradient text-black flex items-center justify-center flex-shrink-0 font-bold text-sm">{i + 1}</div>
                <div className="text-[#D0D0D0] pt-0.5">{s}</div>
              </div>
            ))}
          </div>
          <button data-testid="fc-steps-cta" onClick={cta} className="btn-gold px-7 py-3 rounded-full text-base font-medium mt-8 flex items-center gap-2">
            <Check size={18} /> I'm in — set me up
          </button>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          className="rounded-3xl overflow-hidden border border-[#2A2A2A]">
          <img src={CREATOR_IMG} alt="Founding creator" className="w-full h-full object-cover" />
        </motion.div>
      </section>

      {/* Closing banner */}
      <section className="relative px-6 md:px-12 py-20 overflow-hidden mt-8">
        <div className="absolute inset-0">
          <img src={BANNER} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, #0A0A0A 20%, rgba(10,10,10,0.5))" }} />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-5xl mb-4">Your Empire starts now</h2>
          <p className="text-[#D0D0D0] mb-8 max-w-xl mx-auto">
            Founding spots are limited. Lock in free membership, keep 100%, and be one of the faces that launches Pleasure Empire.
          </p>
          <button data-testid="fc-final-cta" onClick={cta} className="btn-gold px-8 py-3.5 rounded-full text-lg font-medium">
            Claim your Founding spot 👑
          </button>
        </div>
      </section>
    </div>
  );
}
